/**
 * ComplianceDocsPicker — shared multi-select for in-house compliance
 * documents.
 *
 * Used by:
 *   - JobsPage Create dialog (admin picks docs to link upfront)
 *   - JobDetailPage "Attach Compliance" dialog (admin links extra docs to
 *     an existing job — useful when site safety requirements change after
 *     the booking goes in)
 *
 * Renders a search box + checkbox list grouped by category. Selected docs
 * bubble up via `onChange`. The caller is responsible for filtering out
 * docs that are already linked when appropriate (the picker doesn't know
 * about "already-linked" — it just renders the list it's handed).
 */
import { useMemo, useState } from "react";
import { Input } from "./ui/input";

const CATEGORY_LABEL = {
  rams: "RAMS",
  risk_assessment: "Risk Assessment",
  method_statement: "Method Statement",
  coshh: "COSHH",
  safety_data_sheet: "Safety Data Sheet",
  health_and_safety: "Health & Safety",
  company_documents: "Company Documents",
};

export default function ComplianceDocsPicker({ docs, selectedIds, onChange }) {
  const [q, setQ] = useState("");
  const search = q.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!search) return docs;
    return docs.filter(d => {
      const hay = `${d.title || ""} ${d.notes || ""} ${(d.tags || []).join(" ")} ${d.category || ""}`.toLowerCase();
      return hay.includes(search);
    });
  }, [docs, search]);

  // Group by category so the picker reads naturally — RAMS / COSHH / etc.
  const grouped = useMemo(() => {
    const m = {};
    filtered.forEach(d => {
      const cat = d.category || "uncategorised";
      if (!m[cat]) m[cat] = [];
      m[cat].push(d);
    });
    return m;
  }, [filtered]);

  const toggle = (id) => {
    if (selectedIds.includes(id)) onChange(selectedIds.filter(x => x !== id));
    else onChange([...selectedIds, id]);
  };

  return (
    <div className="border border-border rounded-md p-2 mt-1">
      <div className="flex items-center gap-2 mb-2">
        <Input
          placeholder="Search by title, tag…"
          value={q}
          onChange={e => setQ(e.target.value)}
          className="h-8 text-sm"
          data-testid="compliance-picker-search"
        />
        {selectedIds.length > 0 && (
          <span
            className="text-[11px] bg-primary text-primary-foreground rounded-full px-2 py-0.5 whitespace-nowrap"
            data-testid="compliance-picker-selected-count"
          >
            {selectedIds.length} selected
          </span>
        )}
      </div>
      <div className="space-y-2 max-h-44 overflow-y-auto" data-testid="compliance-picker">
        {Object.keys(grouped).length === 0 ? (
          <p className="text-xs text-muted-foreground italic px-2 py-1">No documents match.</p>
        ) : (
          Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1 px-1">
                {CATEGORY_LABEL[cat] || cat}
              </p>
              <div className="space-y-1">
                {items.map(d => {
                  const reviewBadge =
                    d.review_status === "overdue" ? "🔴 overdue"
                    : d.review_status === "due" ? "🟡 due soon"
                    : d.review_status === "ok" ? "🟢 in date"
                    : "";
                  return (
                    <label
                      key={d.id}
                      className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/30 px-2 py-1 rounded"
                      data-testid={`compliance-picker-doc-${d.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(d.id)}
                        onChange={() => toggle(d.id)}
                        className="rounded"
                      />
                      <span className="truncate flex-1" title={d.title}>{d.title}</span>
                      {reviewBadge && (
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">{reviewBadge}</span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
